import React from "react";
import PageHeader from "../components/PageHeader";
import { ShieldCheck, Lock, Globe, Database, Mail } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <>
      <PageHeader
        title="Privacy & Anti-Spam Policy"
        subtitle="Transparency. Compliance. Trust — That’s the AQUIRAA Promise."
      />

      <div className="w-full h-[3px] bg-gradient-to-r from-transparent via-[#00C4CC] to-transparent animate-pulseGlow mb-10"></div>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-14">

        {/* Intro */}
        <div className="bg-white shadow-md rounded-2xl p-8 border border-gray-100">
          <p className="text-gray-700 leading-7 text-[16px]">
            AQUIRAA (“Company”, “we”, “our”) is a U.S.-registered data and marketing
            solutions provider. This Privacy & Anti-Spam Policy outlines how we
            collect, use, protect, and maintain email compliance in accordance with
            global data protection laws including GDPR, CCPA, and CAN-SPAM.
          </p>
        </div>

        {/* Section 1 – About AQUIRAA */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              1. About AQUIRAA
            </h2>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-gray-700 leading-7">
            AQUIRAA is one of the leading custom-business data providers in the U.S.A.
            Our database of 35+ million business contacts is updated daily by a team
            of experienced research and marketing professionals.  
            <br /><br />
            We specialize in gathering, structuring, validating, and verifying global
            B2B information for marketing, sales, and outreach purposes.
          </div>
        </div>

        {/* Section 2 – Information We Collect */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Database className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              2. Information We Collect
            </h2>
          </div>

          <div className="bg-white shadow p-6 rounded-xl border border-gray-200">
            <ul className="space-y-2 text-gray-700 text-[15px] leading-6">
              <li>• Personal details such as name, email, phone number, company</li>
              <li>• Voluntarily provided B2B contact information</li>
              <li>• Website usage analytics, cookies, IP address, device/browser data</li>
              <li>• CRM or form-submission data shared by clients</li>
              <li>• Magazine subscriptions, trade shows, directories, government archives</li>
              <li>• Web forms that legally permit third-party marketing</li>
            </ul>
          </div>
        </div>

        {/* Section 3 – How Information Is Used */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              3. How Your Information Is Used
            </h2>
          </div>

          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-gray-700 leading-7">
            We use your information strictly for legitimate business purposes:
            <ul className="mt-3 space-y-2 text-[15px]">
              <li>• To respond to inquiries and deliver requested services</li>
              <li>• To perform data-driven marketing operations</li>
              <li>• To maintain and update databases</li>
              <li>• To improve website functionality and user experience</li>
              <li>• To send communication you have opted into</li>
            </ul>
          </div>
        </div>

        {/* Section 4 – Data Security */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Lock className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              4. Data Protection & Security
            </h2>
          </div>

          <p className="text-gray-700 leading-7 bg-white shadow p-6 rounded-xl border border-gray-200">
            AQUIRAA uses SSL encryption, secure servers, firewalls, and strict
            internal controls to protect your data. Credit card details are NEVER
            stored permanently.  
            <br /><br />
            While we follow industry-standard best practices, no digital system can
            guarantee 100% security.
          </p>
        </div>

        {/* Section 5 – Subscribers */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              5. Subscribers & User Rights
            </h2>
          </div>

          <div className="text-gray-700 bg-gray-50 p-6 rounded-xl border border-gray-200 leading-7">
            Subscribers may request access, updates, or removal of their information at:
            <br />
            <strong>Email:</strong> subscribers@aquiraa.com  
            <br /><br />
            All emails sent via AQUIRAA contain an unsubscribe link.  
            We NEVER share, sell, or rent personal information without permission,
            unless required by law.
          </div>
        </div>

        {/* Section 6 – Anti-Spam Policy */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Globe className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              6. AQUIRAA Anti-Spam Policy
            </h2>
          </div>

          <div className="bg-white shadow p-6 rounded-xl border border-gray-200 text-gray-700 leading-7">
            AQUIRAA strictly prohibits the sending of unsolicited emails (“spam”).
            <br /><br />
            Our clients may ONLY contact individuals who have:
            <ul className="mt-3 space-y-2 text-[15px]">
              <li>• Opted-in or shown interest</li>
              <li>• Provided consent via legal channels</li>
              <li>• Been processed under permitted marketing frameworks</li>
            </ul>
            Violations lead to immediate termination of services.
          </div>
        </div>

        {/* Section 7 – Email Content Review */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              7. Content Review & Compliance
            </h2>
          </div>

          <p className="text-gray-700 leading-7 bg-gray-50 p-6 rounded-xl border border-gray-200">
            AQUIRAA reviews email campaigns using internal compliance tools and
            algorithms to identify violations, reduce bounce rates, and enhance
            deliverability.
          </p>
        </div>

        {/* Section 8 – Opt-Out Policy */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              8. Opt-Out / Unsubscribe Policy
            </h2>
          </div>

          <div className="text-gray-700 bg-white shadow p-6 rounded-xl border border-gray-200 leading-7">
            Upon request, AQUIRAA will:
            <ul className="mt-3 space-y-2 text-[15px]">
              <li>• Remove subscribers from all future promotional emails</li>
              <li>• Update/correct erroneous information</li>
              <li>• Delete user data to the extent technically possible</li>
            </ul>
            Opt-outs are processed within <strong>5 business days</strong>.
          </div>
        </div>

        {/* Section 9 – Changes to Privacy Policy */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              9. Changes to This Policy
            </h2>
          </div>

          <p className="text-gray-700 leading-7 bg-gray-50 p-6 rounded-xl border border-gray-200">
            AQUIRAA may modify this policy at any time.  
            Users and premium clients will be notified of major updates via email.
          </p>
        </div>

        {/* Section 10 – Contact */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="text-aquiraaBlue" size={26} />
            <h2 className="text-2xl font-semibold text-[#0A1E3F]">
              10. Contact Information
            </h2>
          </div>

          <p className="text-gray-700 leading-7">
            For privacy or compliance inquiries, contact:
            <br />
            <strong>Email:</strong> info@aquiraa.com <br />
            <strong>Address:</strong> 30 N Gould St, STE R, Sheridan, WY 82801, USA
          </p>
        </div>

      </div>

      <style>{`
        @keyframes pulseGlow {
          0% { opacity: 0.3; }
          50% { opacity: 1; }
          100% { opacity: 0.3; }
        }
        .animate-pulseGlow {
          animation: pulseGlow 2.2s infinite ease-in-out;
        }
      `}</style>
    </>
  );
}
